import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ClipboardList, Filter, Search, CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const AgentDashboard = () => {
  const { user } = useAuth();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Tous');

  useEffect(() => {
    fetchDemandes();
  }, []);

  const fetchDemandes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/demandes/all`, { withCredentials: true });
      setDemandes(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des dossiers');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/demandes/${id}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchDemandes();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Reçue':
        return <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">Nouveau</span>;
      case 'En évaluation':
        return <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-200">En cours</span>;
      case 'Clôturée':
        return <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200">Résolu</span>;
      default:
        return <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">{status}</span>;
    }
  };

  const filteredDemandes = demandes.filter(demande => {
    const matchesSearch = (demande.reference?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
                          (demande.sujet?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'Tous' || demande.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F]">Espace Agent Provincial</h1>
          <p className="text-slate-500 font-medium">Bureau de traitement - {user?.name}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[400px]">
        <div className="p-5 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            File d'attente globale
          </h2>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-grow sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="text" 
                placeholder="Référence ou Sujet..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-amber-500" 
              />
            </div>
            <select 
              className="text-slate-600 bg-white border border-slate-200 px-3 py-2 rounded-md text-sm font-semibold flex items-center shadow-sm focus:outline-none"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="Tous">Tous les statuts</option>
              <option value="Reçue">Nouveau</option>
              <option value="En évaluation">En cours</option>
              <option value="Clôturée">Résolu</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="p-12 text-center flex items-center justify-center">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
          </div>
        ) : demandes.length === 0 ? (
          <div className="p-16 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 text-slate-400">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Aucun dossier en attente</h3>
            <p className="text-slate-500 max-w-sm mx-auto text-sm">
              La file est vide. Excellent travail !
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="px-6 py-4">Référence</th>
                  <th className="px-6 py-4">Sujet / Secteur</th>
                  <th className="px-6 py-4">Localisation</th>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">Statut</th>
                  <th className="px-6 py-4">Pièce jointe</th>
                  <th className="px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDemandes.map(demande => (
                  <tr key={demande.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`agent-demande-row-${demande.reference}`}>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm font-bold text-[#1E3A8A] bg-blue-50 px-2 py-1 rounded border border-blue-100">
                        {demande.reference}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">{demande.sujet}</div>
                      <div className="text-xs text-slate-500 font-semibold">{demande.secteur || 'Signalement'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-600 line-clamp-1">{demande.localisation}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-600">{new Date(demande.created_at).toLocaleDateString('fr-FR')}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(demande.status)}
                    </td>
                    <td className="px-6 py-4">
                      {demande.fichier_joint ? (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${demande.fichier_joint}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 text-xs font-semibold underline">
                          Voir le fichier
                        </a>
                      ) : (
                        <span className="text-slate-400 text-xs italic">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {demande.status === 'Reçue' && (
                        <Button 
                          size="sm" 
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
                          onClick={() => updateStatus(demande.id, 'En évaluation')}
                          data-testid={`btn-eval-${demande.id}`}
                        >
                          Prendre en charge
                        </Button>
                      )}
                      {demande.status === 'En évaluation' && (
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                          onClick={() => updateStatus(demande.id, 'Clôturée')}
                          data-testid={`btn-close-${demande.id}`}
                        >
                          Clôturer
                        </Button>
                      )}
                      {demande.status === 'Clôturée' && (
                        <Button variant="outline" size="sm" className="h-8 text-xs font-semibold" disabled>
                          Terminé
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboard;
